import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Email as EmailIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ParentPortalLayout from '../components/ParentPortalLayout';
import { parentPortalAPI } from '../utils/api';
import { useParentAuth } from '../hooks/useParentAuth';
import { parentPortalPrimaryButtonSx, parentPortalSolidButtonSx, parentPortalTheme } from '../theme/parentPortalTheme';

const ParentSettings: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'parentSettings' });
  const navigate = useNavigate();
  const { markPasswordChanged } = useParentAuth();
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', new_password_confirmation: '' });
  const [email, setEmail] = useState('');
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const passwordType = (key: string) => showPasswords[key] ? 'text' : 'password';
  const endAdornment = (key: string) => (
    <InputAdornment position="end">
      <IconButton onClick={() => setShowPasswords(current => ({ ...current, [key]: !current[key] }))} edge="end">
        {showPasswords[key] ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const submitPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    if (passwordForm.new_password !== passwordForm.new_password_confirmation) {
      setError(t('passwordsDoNotMatch'));
      return;
    }
    setLoading(true);
    try {
      const response = await parentPortalAPI.changePassword(passwordForm);
      setMessage(response.message || t('passwordUpdated'));
      setPasswordForm({ current_password: '', new_password: '', new_password_confirmation: '' });
      markPasswordChanged();
      navigate('/parent/dashboard', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('passwordFailed'));
    } finally {
      setLoading(false);
    }
  };

  const submitEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      await parentPortalAPI.changeEmail(email);
      setMessage(t('emailSent'));
      setEmail('');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('emailFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ParentPortalLayout title={t('title')}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 6, bgcolor: parentPortalTheme.settings.passwordCard }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, textTransform: 'uppercase', mb: 2 }}>{t('changePassword')}</Typography>
              <Box component="form" onSubmit={submitPassword}>
                <TextField fullWidth required type={passwordType('current')} label={t('oldPassword')} margin="normal" value={passwordForm.current_password} onChange={(event) => setPasswordForm({ ...passwordForm, current_password: event.target.value })} InputProps={{ endAdornment: endAdornment('current') }} />
                <TextField fullWidth required type={passwordType('new')} label={t('newPassword')} helperText={t('minHint')} margin="normal" value={passwordForm.new_password} onChange={(event) => setPasswordForm({ ...passwordForm, new_password: event.target.value })} InputProps={{ endAdornment: endAdornment('new') }} />
                <TextField fullWidth required type={passwordType('confirm')} label={t('confirmPassword')} margin="normal" value={passwordForm.new_password_confirmation} onChange={(event) => setPasswordForm({ ...passwordForm, new_password_confirmation: event.target.value })} InputProps={{ endAdornment: endAdornment('confirm') }} />
                <Button type="submit" disabled={loading} sx={{ mt: 2, px: 4, ...parentPortalSolidButtonSx }}>
                  {t('updatePassword')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 6, bgcolor: parentPortalTheme.settings.emailCard }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, textTransform: 'uppercase', mb: 2 }}>{t('changeEmail')}</Typography>
              <Box component="form" onSubmit={submitEmail}>
                <TextField fullWidth required type="email" label={t('newEmail')} placeholder={t('emailPlaceholder')} margin="normal" value={email} onChange={(event) => setEmail(event.target.value)} />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: parentPortalTheme.settings.emailNote, my: 2 }}>
                  <EmailIcon fontSize="small" />
                  <Typography>{t('emailNote')}</Typography>
                </Box>
                <Button type="submit" disabled={loading} sx={{ mt: 2, borderRadius: 999, px: 4, color: '#fff', ...parentPortalPrimaryButtonSx }}>
                  {t('updateEmail')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </ParentPortalLayout>
  );
};

export default ParentSettings;
